import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import CustomButton from "../../component/common/Button/Button";
import { themeOrange, themeBlue } from "../../utils/colorTheme";
import { useNavigate } from "react-router-dom";
import { VendorTable } from "../../component/common/customTable/VendorTable";
import { useDispatch, useSelector } from "react-redux";
import { get_users_list_by_role } from "../../services/DashboardUsers";
import { asyncStatus } from "../../utils/asyncStatus";
import { IoAdd, IoSearch } from "react-icons/io5";
import { FiUsers } from "react-icons/fi";
import { MdCheckCircle, MdPending, MdCancel } from "react-icons/md";

const DashboardVendor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");

  const {
    get_users_list_by_role_status,
    get_users_list_by_role_data,
  } = useSelector((state) => state.dashboard_users);

  useEffect(() => {
    dispatch(get_users_list_by_role(2));
  }, []);

  const stats = useMemo(() => {
    const vendors = get_users_list_by_role_data || [];
    return {
      total: vendors.length,
      approved: vendors.filter((v) => +v.status === 1).length,
      pending: vendors.filter((v) => +v.status === 0).length,
      rejected: vendors.filter((v) => +v.status === 2).length,
    };
  }, [get_users_list_by_role_data]);

  if (get_users_list_by_role_status === asyncStatus.LOADING) {
    return (
      <Stack alignItems="center" justifyContent="center" height="60vh">
        <CircularProgress size={30} sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  const statCards = [
    { label: "Total Pros", value: stats.total, icon: <FiUsers size={20} />, bg: "#EDE7F6", color: "#5E35B1" },
    { label: "Approved", value: stats.approved, icon: <MdCheckCircle size={20} />, bg: "#E8F5E9", color: "#2E7D32" },
    { label: "Pending", value: stats.pending, icon: <MdPending size={20} />, bg: "#FFF8E1", color: "#F9A825" },
    { label: "Rejected", value: stats.rejected, icon: <MdCancel size={20} />, bg: "#FFEBEE", color: "#C62828" },
  ];

  return (
    <Stack sx={{ bgcolor: "#F8F9FA", minHeight: "100vh" }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2.5 }}
      >
        <Stack>
          <Typography sx={{ fontSize: "24px", fontWeight: 700, color: themeBlue }}>
            Pros
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#999", mt: 0.3 }}>
            Manage and monitor all service pros
          </Typography>
        </Stack>
        <Box
          onClick={() => navigate("/vendor/add-vendor")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "linear-gradient(135deg, #F15A24 0%, #C53F10 100%)",
            color: "#fff",
            px: 2.5,
            py: 1.2,
            borderRadius: "10px",
            cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(241,90,36,0.35)" },
          }}
        >
          <IoAdd size={18} />
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>Add Pro</Typography>
        </Box>
      </Stack>

      <Container sx={{ maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" } }}>
        {/* Stats Cards */}
        <Grid container spacing={2} mb={3}>
          {statCards.map((stat, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "14px",
                  p: 2.5,
                  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                  transition: "all 0.2s",
                  "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.1)" },
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "12px",
                    bgcolor: stat.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: stat.color,
                    flexShrink: 0,
                  }}
                >
                  {stat.icon}
                </Box>
                <Stack>
                  <Typography sx={{ fontSize: "22px", fontWeight: 700, color: themeBlue, lineHeight: 1.1 }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#999", fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          ))}
        </Grid>

        {/* Search Bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <TextField
            size="small"
            placeholder="Search pros..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IoSearch color="#999" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "320px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: "#fff",
                "&.Mui-focused fieldset": { borderColor: themeOrange },
                "&:hover fieldset": { borderColor: themeOrange },
              },
            }}
          />
          <Typography sx={{ fontSize: "13px", color: "#999" }}>
            {get_users_list_by_role_data?.length || 0} pros total
          </Typography>
        </Stack>

        {/* Table */}
        <Stack
          sx={{
            bgcolor: "#fff",
            borderRadius: "14px",
            boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            overflow: "hidden",
            mb: 4,
          }}
        >
          <VendorTable searchQuery={searchQuery} />
        </Stack>
      </Container>
    </Stack>
  );
};

export default DashboardVendor;
